import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/axios'
import { useCartStore } from '@/store/cartStore'
import { useSessionStore } from '@/store/sessionStore'
import type { CartItem } from '@/types/cart'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface OrderResponse {
  success: boolean
  data: {
    orderId: string
    items: CartItem[]
    total: number
    status: string
  }
  error?: {
    code: string
    message: string
  }
}

interface AddToCartPayload {
  menuItemId: string
  quantity: number
  specialInstructions?: string
  // Menu item data for optimistic update
  menuItem?: {
    name: string
    nameAr?: string
    unitPrice: number
    imageUrl?: string
  }
}

interface UpdateQuantityPayload {
  itemId: string
  quantity: number
}

/**
 * Helper to show toast notifications
 * Note: These functions should be called within React components/hooks that have access to useToast
 */
const createErrorToast = (title: string, message: string) => ({
  variant: 'destructive' as const,
  title,
  description: message,
})

const createSuccessToast = (message: string) => ({
  variant: 'success' as const,
  title: 'Success',
  description: message,
})

/**
 * Transform backend order item to CartItem format
 * Backend returns: { id, menuItemId, quantity, price, menuItemName, menuItemNameAr, menuItemImageUrl, ... }
 * Frontend expects: { id, menuItemId, quantity, unitPrice, name, nameAr, imageUrl, lineTotal, ... }
 */
function transformOrderItemToCartItem(item: any): CartItem {
  return {
    id: item.id, // Order item ID (UUID) - this is what we need for deletion
    menuItemId: item.menuItemId,
    name: item.menuItemName || item.name || '',
    nameAr: item.menuItemNameAr || item.nameAr || '',
    quantity: item.quantity,
    unitPrice: item.price || item.unitPrice || 0, // Backend uses 'price', frontend uses 'unitPrice'
    lineTotal: (item.price || item.unitPrice || 0) * item.quantity, // Calculate lineTotal
    imageUrl: item.menuItemImageUrl || item.imageUrl,
    specialInstructions: item.specialInstructions,
  }
}

/**
 * useOrder - Fetch order with items from backend
 */
export function useOrder(orderId: string | null) {
  return useQuery<OrderResponse['data'], Error>({
    queryKey: ['order', orderId],
    queryFn: async () => {
      if (!orderId) throw new Error('No orderId provided')
      const response = await apiClient.get<OrderResponse>(
        `/api/v1/public/orders/${orderId}`
      )
      // Transform backend items to CartItem format
      return {
        ...response.data.data,
        items: response.data.data.items.map(transformOrderItemToCartItem),
      }
    },
    enabled: !!orderId,
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
    refetchOnWindowFocus: true,
    refetchInterval: 30000, // Refetch every 30 seconds
    retry: 1,
  })
}

/**
 * useAddToCart - Add item to cart mutation
 */
export function useAddToCart() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationKey: ['cart', 'add'],
    mutationFn: async (payload: AddToCartPayload) => {
      // Get orderId and sessionId from sessionStore - validate they match
      const { orderId, sessionId } = useSessionStore.getState()
      if (!orderId) throw new Error('No order found. Please start a new session.')
      if (!sessionId) throw new Error('No session found. Please start a new session.')

      // Strip menuItem from payload - it's only used for optimistic updates, not sent to backend
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { menuItem, ...backendPayload } = payload

      const response = await apiClient.post<OrderResponse>(
        `/api/v1/public/orders/${orderId}/items`,
        backendPayload
      )

      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to add item to cart')
      }

      return response.data.data
    },
    onMutate: async (payload) => {
      // Optimistic update - add to local store immediately
      // Use menu item data if provided, otherwise show loading state
      if (payload.menuItem) {
        const { addItem: storeAddItem } = useCartStore.getState()
        storeAddItem({
          id: `temp-${Date.now()}`,
          menuItemId: payload.menuItemId,
          name: payload.menuItem.name,
          nameAr: payload.menuItem.nameAr || '',
          quantity: payload.quantity,
          unitPrice: payload.menuItem.unitPrice,
          imageUrl: payload.menuItem.imageUrl,
        })
      }
      // If no menu item data, skip optimistic update (will update on success)
    },
    onSuccess: (data) => {
      // Sync with server response - transform backend items to CartItem format
      const { setOrder } = useCartStore.getState()
      setOrder({
        orderId: data.orderId,
        items: data.items.map(transformOrderItemToCartItem),
        total: data.total,
      })

      // Invalidate order query
      const orderId = useSessionStore.getState().orderId
      if (orderId) {
        queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      }

      toast(createSuccessToast('Item added to cart'))
    },
    onError: (error, payload) => {
      // Rollback optimistic update - remove temp item if it exists
      const { items, removeItem } = useCartStore.getState()
      const tempItem = items.find(
        (item) => item.menuItemId === payload.menuItemId && item.id.startsWith('temp-')
      )
      if (tempItem) {
        removeItem(tempItem.id)
      }

      // Also refetch to ensure sync
      const orderId = useSessionStore.getState().orderId
      if (orderId) {
        queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      }

      toast(createErrorToast(
        'Error',
        error instanceof Error ? error.message : 'Failed to add item to cart'
      ))
    },
    retry: 1,
  })
}

/**
 * useUpdateCartItem - Update item quantity mutation
 */
export function useUpdateCartItem() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationKey: ['cart', 'update'],
    mutationFn: async (payload: UpdateQuantityPayload) => {
      const { orderId, sessionId } = useSessionStore.getState()
      if (!orderId) throw new Error('No order found. Please start a new session.')
      if (!sessionId) throw new Error('No session found. Please start a new session.')

      const response = await apiClient.patch<OrderResponse>(
        `/api/v1/public/orders/${orderId}/items/${payload.itemId}`,
        { quantity: payload.quantity }
      )

      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to update item quantity')
      }

      return response.data.data
    },
    onMutate: async (payload) => {
      // Store previous value for rollback
      const { getItemById, updateQuantity } = useCartStore.getState()
      const previousItem = getItemById(payload.itemId)
      const previousQuantity = previousItem?.quantity

      // Optimistic update
      updateQuantity(payload.itemId, payload.quantity)

      return { previousQuantity, itemId: payload.itemId }
    },
    onSuccess: (data) => {
      // Sync with server response - transform backend items to CartItem format
      const { setOrder } = useCartStore.getState()
      setOrder({
        orderId: data.orderId,
        items: data.items.map(transformOrderItemToCartItem),
        total: data.total,
      })

      // Invalidate order query
      const orderId = useSessionStore.getState().orderId
      if (orderId) {
        queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      }
    },
    onError: (error, payload, context) => {
      // Rollback to previous quantity
      if (context?.previousQuantity) {
        const { updateQuantity } = useCartStore.getState()
        updateQuantity(payload.itemId, context.previousQuantity)
      }

      toast(createErrorToast(
        'Error',
        error instanceof Error ? error.message : 'Failed to update item quantity'
      ))
    },
    retry: 1,
  })
}

/**
 * useRemoveCartItem - Remove item from cart mutation
 */
export function useRemoveCartItem() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationKey: ['cart', 'remove'],
    mutationFn: async (itemId: string) => {
      // Prevent deletion of items with temporary IDs (from optimistic updates)
      if (itemId.startsWith('temp-')) {
        throw new Error('Item is still being added. Please wait a moment and try again.')
      }

      const { orderId, sessionId } = useSessionStore.getState()
      if (!orderId) throw new Error('No order found. Please start a new session.')
      if (!sessionId) throw new Error('No session found. Please start a new session.')

      const response = await apiClient.delete<OrderResponse>(
        `/api/v1/public/orders/${orderId}/items/${itemId}`
      )

      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to remove item from cart')
      }

      return response.data.data
    },
    onMutate: async (itemId) => {
      // Store previous item for rollback
      const { removeItem, getItemById } = useCartStore.getState()
      const previousItem = getItemById(itemId)

      // Optimistic update
      removeItem(itemId)

      return { previousItem }
    },
    onSuccess: (data) => {
      // Sync with server response - transform backend items to CartItem format
      const { setOrder } = useCartStore.getState()
      setOrder({
        orderId: data.orderId,
        items: data.items.map(transformOrderItemToCartItem),
        total: data.total,
      })

      // Invalidate order query
      const orderId = useSessionStore.getState().orderId
      if (orderId) {
        queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      }

      toast(createSuccessToast('Item removed from cart'))
    },
    onError: (error, _itemId, context) => {
      // Rollback by adding item back
      if (context?.previousItem) {
        useCartStore.getState().addItem(context.previousItem)
      }

      toast(createErrorToast(
        'Error',
        error instanceof Error ? error.message : 'Failed to remove item from cart'
      ))
    },
    retry: 1,
  })
}

/**
 * useSubmitOrder - Submit order mutation
 * Changes order status from draft to submitted
 */
export function useSubmitOrder() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationKey: ['order', 'submit'],
    mutationFn: async (orderId: string) => {
      // Validate session before submission
      const { isSessionValid } = useSessionStore.getState()
      if (!isSessionValid()) {
        throw new Error('Your session has expired. Please scan QR code again.')
      }

      const response = await apiClient.post<{
        success: boolean
        data: {
          orderId: string
          status: string
          submittedAt: string
          eta: number
        }
        error?: {
          code: string
          message: string
        }
      }>(`/api/v1/public/orders/${orderId}/submit`, {})

      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to submit order')
      }

      return response.data.data
    },
    onSuccess: (data) => {
      // Clear cart after successful submission
      useCartStore.getState().clearCart()

      // Invalidate order query
      queryClient.invalidateQueries({ queryKey: ['order', data.orderId] })

      toast({
        variant: 'success',
        title: 'Order Confirmed!',
        description: `Order #${data.orderId.substring(0, 8)} confirmed. Ready in ${data.eta} minutes.`,
      })
    },
    onError: (error) => {
      toast(createErrorToast(
        'Submission Failed',
        error instanceof Error ? error.message : 'Failed to submit order. Please try again.'
      ))
    },
    retry: 1,
  })
}

/**
 * useClearCart - Clear all items from cart mutation
 * Uses new backend endpoint DELETE /api/v1/public/orders/:orderId/items
 */
export function useClearCart() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationKey: ['cart', 'clear'],
    mutationFn: async () => {
      const { orderId, sessionId } = useSessionStore.getState()
      if (!orderId) throw new Error('No order found. Please start a new session.')
      if (!sessionId) throw new Error('No session found. Please start a new session.')

      // Use new backend endpoint to clear all items in one call
      const response = await apiClient.delete<OrderResponse>(
        `/api/v1/public/orders/${orderId}/items`
      )

      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to clear cart')
      }

      return response.data.data
    },
    onMutate: async () => {
      // Clear local cart
      useCartStore.getState().clearCart()
    },
    onSuccess: (data) => {
      // Sync with server response - transform backend items to CartItem format
      const { setOrder } = useCartStore.getState()
      setOrder({
        orderId: data.orderId,
        items: data.items.map(transformOrderItemToCartItem),
        total: data.total,
      })

      // Invalidate order query
      const orderId = useSessionStore.getState().orderId
      if (orderId) {
        queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      }

      toast(createSuccessToast('Cart cleared'))
    },
    onError: (error) => {
      // Refetch to restore state
      const orderId = useSessionStore.getState().orderId
      if (orderId) {
        queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      }

      toast(createErrorToast(
        'Error',
        error instanceof Error ? error.message : 'Failed to clear cart'
      ))
    },
    retry: 1,
  })
}
